import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  SimpleChanges,
  OnChanges,
  ViewChild,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { trimLabel } from '../common/trim-label.helper';
import { roundedRect } from '../common/shape.helper';
import { escapeLabel } from '../common/label.helper';
import { decimalChecker, count } from '../common/count/count.helper';
import { GridData } from '../common/grid-layout.helper';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { calculateTextWidth } from '../utils/calculate-width';
import { VERDANA_FONT_WIDTHS_16_PX } from '../common/constants/font-widths';


@Component({
  selector: 'g[ngx-charts-card]',
  template: `
    <svg:g [attr.transform]="transform" class="cell" (click)="onClick()">
      <svg:rect class="card" [style.fill]="color" [attr.width]="cardWidth" [attr.height]="cardHeight" rx="3" ry="3" />
      <svg:path
        *ngIf="bandColor && bandColor !== color"
        class="card-band"
        [attr.fill]="bandColor"
        [attr.transform]="transformBand"
        stroke="none"
        [attr.d]="bandPath"
      />
      <title>{{ label }}</title>
      <svg:foreignObject  [attr.x]="0"
                          [attr.y]="0"
                          [attr.width]="cardWidth"
                          [attr.height]="cardHeight">
        <wiz-vgroup #wizGP height="100%" width="100%" verticalAlign="center" horizontalAlign="center" style="justify-content: center; padding: 5px">
          <wiz-label 
            [style.color]="textColor"
            [wizLabel]="value" width="100%" 
            [style.scale]="wizScale"
            [style.text-align]="'center'"
            [wizTextBehavior]="'truncate'" style="font-size: 20px">
          </wiz-label>
          <wiz-label *ngIf="wizScale > 0.6"
            [style.color]="textColor"
            [wizLabel]="label" width="100%"
            [style.text-align]="'center'"
            [wizNbLine]="2" style="font-size: 11px;">
          </wiz-label>
        </wiz-vgroup>
      </svg:foreignObject>
    </svg:g>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardComponent implements OnChanges, OnDestroy {
  @Input() color: string;
  @Input() bandColor: string;
  @Input() textColor: string;
  @Input() x: number;
  @Input() y: number;
  @Input() width: number;
  @Input() height: number;
  @Input() label: string;
  @Input() data: GridData;
  @Input() medianSize: number;
  @Input() valueFormatting: any;
  @Input() labelFormatting: any;
  @Input() formatedValue: any;
  @Input() animations: boolean = true;

  @Output() select = new EventEmitter();

  @ViewChild('textEl', { static: false }) textEl: ElementRef;

  element: HTMLElement;
  value: string = '';
  transform: string;
  formattedLabel: string;
  cardWidth: number;
  cardHeight: number;
  textWidth: number;
  textFontSize: number = 12;
  textTransform: string = '';
  initialized: boolean = false;
  animationReq: number;

  bandHeight: number = 10;
  transformBand: string;
  textPadding: number[] = [10, 20, 5, 20];
  labelFontSize: number = 15;

  bandPath: string;

  wizScale: number = 1;

  constructor(
    element: ElementRef,
    private cd: ChangeDetectorRef,
    private zone: NgZone,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.element = element.nativeElement;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.update();
  }

  ngOnInit() {
    if (isPlatformServer(this.platformId)) {
      this.scaleTextSSR();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      cancelAnimationFrame(this.animationReq);
    }
  }

  update(): void {
    this.zone.run(() => {
      const hasValue = this.data && typeof this.data.value !== 'undefined';
      const labelFormatting = this.labelFormatting || ((card) => escapeLabel(trimLabel(card.label, 55)));

      this.transform = `translate(${this.x} , ${this.y})`;

      this.textWidth = Math.max(0, this.width) - this.textPadding[1] - this.textPadding[3];
      this.cardWidth = Math.max(0, this.width);
      this.cardHeight = Math.max(0, this.height);

      this.label = this.label ? this.label : (this.data.name as any);

      const cardData = {
        label: this.label,
        data: this.data,
        value: this.data.value,
      };

      this.formattedLabel = labelFormatting(cardData);
      this.transformBand = `translate(0 , ${this.cardHeight - this.bandHeight})`;

      const value = hasValue ? this.formatedValue : '';

      this.value = value; // this.paddedValue(value);
      // this.setPadding();

      this.bandPath = roundedRect(0, 0, this.cardWidth, this.bandHeight, 3, [false, false, true, true]);

      setTimeout(() => {
        // if (isPlatformBrowser(this.platformId)) {
          this.doWizScale();
        // }
        this.value = value;
        if (hasValue && !this.initialized) {
          setTimeout(() => this.startCount(), 20);
        }
      }, 8);
    });
  }

  // paddedValue(value: string): string {
  //   if (this.medianSize && this.medianSize > value.length) {
  //     value += '\u2007'.repeat(this.medianSize - value.length);
  //   }
  //   return value;
  // }

  startCount(): void {
    if (!this.initialized && this.animations) {
      cancelAnimationFrame(this.animationReq);

      const val = this.data.value;
      const decs = decimalChecker(val);
      const valueFormatting = this.valueFormatting || ((card) => card.value.toLocaleString());

      const callback = ({ value, finished }) => {
        this.zone.run(() => {
          value = finished ? val : value;
          this.value = valueFormatting({ label: this.label, data: this.data, value });
          if (!finished) {
            this.value = value; // this.paddedValue(this.value);
          }
          this.cd.markForCheck();
        });
      };

      this.animationReq = count(0, val, decs, 1, callback);
      this.initialized = true;
    }
  }

  public doWizScale(): void {
    let requiredWidth = calculateTextWidth(VERDANA_FONT_WIDTHS_16_PX, this.value || '', 20);
    requiredWidth = Math.max(requiredWidth, 20); // pas moins que défault, sinon tout petit
    const realWidth = this.cardWidth - (10 + 10); // - padding + marge
    const realheight = this.cardHeight - 10 -10;// - 30; // - padding - potentiellement 2 lignes
    let xscale = realWidth / (requiredWidth*2);

    let requiredHeight = 20*xscale;
    let yscale = realheight / (requiredHeight);
    let realScale: number = Math.min(xscale, yscale);
    // console.log('realScale', realScale, this.label);
    realScale = Math.max(realScale, 0.6); // pas moins que 0.6
    realScale = Math.min(realScale, 2.5); // et pas plus que 2.5

    // console.log('scale', this.value, xscale, yscale, realScale);
    this.cd.markForCheck();
    // TODO mesurer la hauteur avec base taille police * scale
    // et controle < realheight, sinon prendre ce ratio
    
    this.wizScale = realScale;
  }

  scaleText(): void {
    // this.zone.run(() => {
    //   const { width, height } = this.textEl.nativeElement.getBoundingClientRect();
    //   if (width === 0 || height === 0) {
    //     return;
    //   }

    //   const textPadding = (this.textPadding[1] = this.textPadding[3] = this.cardWidth / 8);
    //   const availableWidth = this.cardWidth - 2 * textPadding;
    //   const availableHeight = this.cardHeight / 3;

    //   const resizeScale = Math.min(availableWidth / width, availableHeight / height);
    //   this.textFontSize = Math.floor(this.textFontSize * resizeScale);
    //   this.labelFontSize = Math.min(this.textFontSize, 11);

    //   this.setPadding();
    //   this.cd.markForCheck();
    // });
  }

  scaleTextSSR() {
    // const width = calculateTextWidth(VERDANA_FONT_WIDTHS_16_PX, this.value, 10);
    // const height = 18;
    // const textPadding = (this.textPadding[1] = this.textPadding[3] = this.cardWidth / 8);
    // const availableWidth = this.cardWidth - 2 * textPadding;
    // const availableHeight = this.cardHeight / 3;

    // const resizeScale = Math.min(availableWidth / width, availableHeight / height);

    // this.textFontSize = Math.floor(this.textFontSize * resizeScale);
    // this.labelFontSize = Math.min(this.textFontSize, 15);

    // this.setPadding();
  }

  // setPadding() {
  //   this.textPadding[1] = this.textPadding[3] = this.cardWidth / 10;
  //   const padding = this.cardHeight / 2;
  //   this.textPadding[0] = padding - this.textFontSize - this.labelFontSize / 2;
  //   this.textPadding[2] = padding - this.labelFontSize;
  // }

  onClick(): void {
    this.select.emit(this.data);
  }
}
